import { CircularProgress, Grid } from '@mui/material';
import { HSpacer, RoutalDialog, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { toDayNoon } from '@nx-smartmonkey/shared/helpers';
import ArrowRightIcon from '../../../assets/svg/ArrowRightIcon';
import DialogInfo from '../../../assets/svg/DialogInfo';
import { NAME_SUBSTITUTION_CHARACTER_START, ProjectPlans } from '../../../domain/project/ProjectPlans';
import { Vehicle } from '../../../domain/vehicle/Vehicle';
import useCreatePlan from '../../../hooks/plan/useCreatePlan';
import { useRetrieveActiveProject } from '../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActiveProjectResources } from '../../../hooks/project/useRetrieveActiveProjectResources';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import Button, { ButtonProps } from '../../../react-components/Button';
import { CalendarPlanCreation } from '../../../react-components/input-date-picker/CalendarPlanCreation';
import InputText from '../../../react-components/InputText';
import Text from '../../../react-components/Text';
import eventsPanel from '../../../services/events';
import { VehiclesSelectionPlanCreation } from '../plan-details/dialogs/VehiclesSelectionPlanCreation';

type CreatePlanProps = {
  buttonProps: ButtonProps;
  handleAfterCreatePlan: (newPlanId: string) => void;
};

export const CreatePlan = ({ buttonProps, handleAfterCreatePlan }: CreatePlanProps) => {
  const { data: user } = useRetrieveUser();
  const { data: projectResources } = useRetrieveActiveProjectResources();
  const { data: activeProject } = useRetrieveActiveProject();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [executionDate, setExecutionDate] = useState<Date>(toDayNoon(new Date()));
  const [selectedVehicles, setSelectedVehicles] = useState<Vehicle[]>([]);
  const [label, setLabel] = useState<string>(``);
  const [isVehiclesLoading, setIsVehiclesLoading] = useState<boolean>(true);
  const {
    run: createPlan,
    data: createdPlanId,
    success: planCreated,
    isLoading: isCreating,
  } = useCreatePlan({ project: activeProject });

  const resultingQuota = () => (projectResources?.max_vehicles_plan ?? 0) - selectedVehicles.length;

  useEffect(() => {
    if (activeProject) {
      setLabel(
        ProjectPlans.labelTemplateToValue({
          template: activeProject.plans.label,
          date: new Date(),
          language: user?.language.value,
        })
      );
      setExecutionDate(activeProject.plans.defaultStartDateToValue());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeProject && activeProject.plans.label.includes(NAME_SUBSTITUTION_CHARACTER_START)) {
      setLabel(
        ProjectPlans.labelTemplateToValue({
          template: activeProject.plans.label,
          date: executionDate,
          language: user?.language.value,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionDate]);

  useEffect(() => {
    if (planCreated && createdPlanId) {
      handleAfterCreatePlan(createdPlanId);
    }
  }, [createdPlanId, handleAfterCreatePlan, planCreated]);

  if (!activeProject) return null;

  const handleCreatePlan = () => {
    const newDrivers = selectedVehicles.map((vehicle: Vehicle) => vehicle.driverFromVehicle());
    eventsPanel.createDrivers(`Desde Nuevo Plan`);
    createPlan({
      label,
      drivers: newDrivers,
      status: activeProject.plans.status,
      executionDate: executionDate ?? activeProject.plans.defaultStartDateToValue(),
    });
  };

  return (
    <>
      <Button
        {...buttonProps}
        disabled={isCreating || dialogOpen}
        onClick={() => {
          setDialogOpen(true);
        }}
      >
        {isCreating && (
          <>
            <Grid container direction="column" justifyContent="center" style={{ height: `100%`, width: `32px` }}>
              <div className="lds-ellipsis-little" style={{ height: `8px` }}>
                <div style={{ top: `2px`, backgroundColor: `white` }} />
                <div style={{ top: `2px`, backgroundColor: `white` }} />
                <div style={{ top: `2px`, backgroundColor: `white` }} />
              </div>
            </Grid>
            <HSpacer small />
          </>
        )}
        <Text
          tagName="span"
          variant="body"
          style={{ color: `white`, cursor: `pointer` }}
          intlId="planner.plans.create_new_plan"
        />
      </Button>

      <RoutalDialog
        id="create-plan-dialog"
        onCloseDialog={() => setDialogOpen(false)}
        open={dialogOpen}
        contentWithOverflow={false}
        contentStyles={{
          height: `100%`,
          width: `100%`,
          padding: `0px`,
        }}
        dialogProps={{ maxWidth: `lg` }}
        dialogContentWarning={<div></div>}
        titleContentStyles={{ padding: `0px 24px` }}
        titleContent={
          <Grid container style={{ display: `flex`, flexWrap: `nowrap`, alignItems: `center` }}>
            <Grid item style={{ whiteSpace: `nowrap`, padding: `0px 0px 0px 8px` }}>
              <Text variant="h6" intlId="create_plan_dialog.title" />
            </Grid>
            <ArrowRightIcon style={{ stroke: RoutalPalette.secondary60, marginBottom: `2px` }} />
            <Grid>
              <InputText
                fieldValue={label}
                onChange={(text: any) => {
                  setLabel(text);
                }}
                fullWidth
                forceFocusedBorderColor
                focusedBorderColor={RoutalPalette.secondary60}
                inputStyles={{ fontSize: `20px` }}
                inputContainerStyles={{
                  padding: 0,
                  alignContent: `center`,
                }}
                variant={`clean`}
                bGColor={`transparent`}
                hoverBGColor={RoutalPalette.neutral05}
                inputProps={{
                  style: {
                    fontSize: `16px`,
                    fontWeight: 500,
                    height: `18px`,
                    overflow: `hidden`,
                    padding: `0px 2px`,
                    marginTop: `4px`, //Evitar el desfase del input
                    color: RoutalPalette.primary80,
                  },
                }}
              />
            </Grid>
          </Grid>
        }
        footerContent={
          <Grid style={{ width: `100%`, display: `flex`, justifyContent: `space-between`, height: `36px` }}>
            <Grid style={{ width: `170px` }} />
            <Grid
              style={{
                opacity: !selectedVehicles.length && !isVehiclesLoading ? 1 : 0,
                display: `flex`,
                alignItems: `center`,
              }}
            >
              <DialogInfo style={{ color: RoutalPalette.primary40 }} />

              <HSpacer small />
              <Text intlId="create_plan_dialog.warning_no_vehicles" />
            </Grid>

            <Button
              disabled={isCreating || resultingQuota() < 0}
              variant={!selectedVehicles.length && !isVehiclesLoading ? `flat` : undefined}
              style={{
                minWidth: `125px`,
                maxWidth: `170px`,
                color: RoutalPalette.primary40,
                margin: `0px`,
              }}
              onClick={() => {
                if (!isCreating && resultingQuota() >= 0) handleCreatePlan();
              }}
            >
              {isCreating ? (
                <CircularProgress size={32} />
              ) : (
                <Text
                  variant="button"
                  intlId="create_plan_dialog.button_create"
                  style={{ color: !selectedVehicles.length && !isVehiclesLoading ? undefined : `white` }}
                />
              )}
            </Button>
          </Grid>
        }
      >
        <Grid container style={{ width: `100%`, display: `flex`, flexWrap: `nowrap`, flexDirection: `row` }}>
          <Grid
            container
            direction="column"
            style={{
              height: `100%`,
              width: `675px`, //440 calendar + 33 padding interior*2 + 24 padding exterior*2
              padding: `24px`,
              border: `1px solid ${RoutalPalette.neutral10}`,
              borderTop: `unset`,
              borderRight: `unset`,
              borderLeft: `unset`,
              display: `flex`,
              flexWrap: `nowrap`,
            }}
          >
            <VSpacer medium />

            <Grid
              style={{
                padding: `33px`,
                paddingBottom: `0px`,
                height: `500px`,
                width: `auto`,
                pointerEvents: isCreating ? `none` : undefined,
              }}
            >
              <CalendarPlanCreation
                dateValue={executionDate}
                onChange={(date: Date) => {
                  setExecutionDate(toDayNoon(date));
                }}
                locale={user?.language.value}
                portalId="create-plan-dialog"
                minDate={new Date()}
              />
            </Grid>
          </Grid>
          <Grid
            container
            direction="column"
            style={{
              height: `565px`,
              width: `auto`,
              padding: `33px`,
              border: `1px solid ${RoutalPalette.neutral10}`,
              borderTop: `unset`,
              borderRight: `unset`,
              pointerEvents: isCreating ? `none` : undefined,
            }}
          >
            <VehiclesSelectionPlanCreation
              selectedVehicles={selectedVehicles}
              setSelectedVehicles={(vehicles) => setSelectedVehicles(vehicles ?? [])}
              showWarning={resultingQuota() < 0}
              executionDate={executionDate ?? new Date()}
              setIsVehiclesLoading={setIsVehiclesLoading}
            />
          </Grid>
        </Grid>
      </RoutalDialog>
    </>
  );
};
